/**
 * Cushion Production Service - Handle fetching and transforming cushion/foam data
 */

import {
  CushionData,
  DailyCushionData,
  CushionFilters,
  CushionStats,
  CushionChartData,
  ProcessData
} from '@/types/cushion';
import { supabase } from '@/integrations/supabase/client';

// Constants
const API_TIMEOUT = 30000; // 30 seconds

/** Bearer header with the current Supabase session, required by the authenticated proxy. */
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch raw cushion data via our authenticated proxy.
 *
 * NOTE: third-party CORS proxy fallbacks (cors.sh, allorigins) were removed —
 * they tunnelled internal production data through external services.
 */
export async function getCushionData(filters: CushionFilters): Promise<CushionData[]> {
  const params = new URLSearchParams();
  params.append('fromDate', filters.fromDate);
  params.append('toDate', filters.toDate);

  if (filters.compID) {
    params.append('compID', filters.compID.toString());
  }

  console.log('🏭 Fetching cushion data with filters:', filters);

  console.log('🔄 Trying Custom Cushion API Proxy...');
  const customProxyUrl = `/api/cushion-data?${params.toString()}`;

  const response = await fetch(customProxyUrl, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    signal: AbortSignal.timeout(API_TIMEOUT)
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ Custom Cushion API Proxy failed:', response.status, errorText);
    throw new Error('Không thể kết nối đến API nệm. Vui lòng kiểm tra kết nối mạng hoặc liên hệ IT support.');
  }

  const data = await response.json();
  console.log('✅ Custom Cushion API Proxy success:', data.length, 'records');
  return data;
}

/**
 * Transform raw API data to daily cushion format
 */
export function transformCushionToDaily(rawData: CushionData[]): DailyCushionData[] {
  return rawData.map(item => ({
    date: item.DocDate.split('T')[0], // Extract date part
    processes: {
      catVai: {
        labor: item.CCatVai || null,
        volume: item.M3CatVai || null,
        productivity: item.NSCatVai || null,
      },
      may: {
        labor: item.CMay || null,
        volume: item.M3May || null,
        productivity: item.NSMay || null,
      },
      thanhPham: {
        labor: item.CTPham || null,
        volume: item.M3TPham || null,
        productivity: item.NSTPham || null,
      },
      doMut: {
        labor: item.CDoMut || null,
        volume: item.M3DoMut || null,
        productivity: item.NSDoMut || null,
      },
      catMut: {
        labor: item.CCatMut || null,
        volume: item.M3CatMut || null,
        productivity: item.NSCatMut || null,
      },
      gon: {
        labor: item.CGon || null,
        volume: null, // Gòn doesn't have volume
        productivity: item.NSGon || null,
        weight: item.KgGon || null, // Gòn has weight instead
      },
    }
  }));
}

/**
 * Calculate cushion statistics from daily data
 */
export function calculateCushionStats(dailyData: DailyCushionData[]): CushionStats {
  if (dailyData.length === 0) {
    return {
      totalDays: 0,
      maxLabor: 0,
      maxVolume: 0,
      avgLabor: 0,
      avgVolume: 0,
      avgProductivity: 0,
      processes: {
        catVai: { totalLabor: 0, totalVolume: 0, avgProductivity: 0 },
        may: { totalLabor: 0, totalVolume: 0, avgProductivity: 0 },
        thanhPham: { totalLabor: 0, totalVolume: 0, avgProductivity: 0 },
        doMut: { totalLabor: 0, totalVolume: 0, avgProductivity: 0 },
        catMut: { totalLabor: 0, totalVolume: 0, avgProductivity: 0 },
        gon: { totalLabor: 0, totalWeight: 0, avgProductivity: 0 },
      }
    };
  }

  const processes = ['catVai', 'may', 'thanhPham', 'doMut', 'catMut', 'gon'] as const;
  
  // Calculate totals for each process
  const processStats = processes.reduce((acc, processKey) => {
    const processData = dailyData.map(day => day.processes[processKey]);
    
    if (processKey === 'gon') {
      // Special handling for Gòn process (uses weight instead of volume)
      acc[processKey] = {
        totalLabor: processData.reduce((sum, p) => sum + (p.labor || 0), 0),
        totalWeight: processData.reduce((sum, p) => sum + (p.weight || 0), 0),
        avgProductivity: processData.filter(p => p.productivity).length > 0 
          ? processData.reduce((sum, p) => sum + (p.productivity || 0), 0) / processData.filter(p => p.productivity).length
          : 0
      };
    } else {
      acc[processKey] = {
        totalLabor: processData.reduce((sum, p) => sum + (p.labor || 0), 0),
        totalVolume: processData.reduce((sum, p) => sum + (p.volume || 0), 0),
        avgProductivity: processData.filter(p => p.productivity).length > 0 
          ? processData.reduce((sum, p) => sum + (p.productivity || 0), 0) / processData.filter(p => p.productivity).length
          : 0
      };
    }
    
    return acc;
  }, {} as any);

  // Calculate overall statistics
  const totalLabor = Object.values(processStats).reduce((sum: number, stat: any) => sum + stat.totalLabor, 0);
  const totalVolume = Object.values(processStats)
    .filter((stat: any) => 'totalVolume' in stat)
    .reduce((sum: number, stat: any) => sum + stat.totalVolume, 0);
  
  const maxLabor = Math.max(...dailyData.map(day => 
    Object.values(day.processes).reduce((sum, p) => sum + (p.labor || 0), 0)
  ));
  
  const maxVolume = Math.max(...dailyData.map(day => 
    Object.values(day.processes)
      .filter(p => p.volume !== undefined)
      .reduce((sum, p) => sum + (p.volume || 0), 0)
  ));

  return {
    totalDays: dailyData.length,
    maxLabor,
    maxVolume,
    avgLabor: dailyData.length > 0 ? totalLabor / dailyData.length : 0,
    avgVolume: dailyData.length > 0 ? totalVolume / dailyData.length : 0,
    avgProductivity: Object.values(processStats).reduce((sum: number, stat: any) => sum + stat.avgProductivity, 0) / processes.length,
    processes: processStats
  };
}

/**
 * Get chart data for cushion processes
 */
export function getCushionChartData(dailyData: DailyCushionData[], metric: 'labor' | 'volume' | 'productivity'): CushionChartData[] {
  return dailyData.map(day => ({
    date: day.date,
    catVai: day.processes.catVai[metric] || 0,
    may: day.processes.may[metric] || 0,
    thanhPham: day.processes.thanhPham[metric] || 0,
    doMut: day.processes.doMut[metric] || 0,
    catMut: day.processes.catMut[metric] || 0,
    gon: metric === 'volume' ? (day.processes.gon.weight || 0) : (day.processes.gon[metric] || 0), // Gòn uses weight for volume metric
  }));
}